import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { fetchJson } from "@/utils/api";
import { useGameStore } from "@/store/gameStore";
import type {
  CreateGameRequest,
  CreateGameResponse,
  ModerationMode,
  WinCondition,
} from "@/types/api";
import WizardStepper from "@/components/create/WizardStepper";
import WizardNav from "@/components/create/WizardNav";
import StepItems from "@/components/create/StepItems";
import StepNameIdentity from "@/components/create/StepNameIdentity";
import StepSettings from "@/components/create/StepSettings";
import Modal from "@/components/ui/Modal";

const AGE_CONFIRMED_KEY = "ispyfr-age-confirmed";

export default function CreateGamePage() {
  const navigate = useNavigate();
  const sessions = useGameStore((s) => s.sessions);
  const addSession = useGameStore((s) => s.addSession);

  const [ageConfirmed, setAgeConfirmed] = useState(
    () => sessionStorage.getItem(AGE_CONFIRMED_KEY) === "true",
  );

  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1);

  // Step 1 state
  const [items, setItems] = useState<{ name: string; description?: string }[]>(
    [],
  );
  const [categoryName, setCategoryName] = useState<string | undefined>();
  const [templateId, setTemplateId] = useState<string | undefined>();

  // Step 2 state
  const mostRecentDisplayName =
    [...sessions].sort((a, b) => b.joinedAt - a.joinedAt)[0]?.displayName ?? "";
  const [displayName, setDisplayName] = useState(mostRecentDisplayName);
  const [title, setTitle] = useState("");
  const [titleManuallyEdited, setTitleManuallyEdited] = useState(false);

  // Step 3 state
  const [winCondition, setWinCondition] =
    useState<WinCondition>("first_complete");
  const [timeLimitMinutes, setTimeLimitMinutes] = useState("30");
  const [moderationMode, setModerationMode] =
    useState<ModerationMode>("any");
  const [votingEnabled, setVotingEnabled] = useState(false);
  const [instructions, setInstructions] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-generate title when displayName or categoryName changes
  useEffect(() => {
    if (titleManuallyEdited) return;
    const name = displayName.trim();
    if (!name) {
      setTitle("");
      return;
    }
    const suffix = categoryName ?? "Scavenger Hunt";
    setTitle(`${name}'s ${suffix}`);
  }, [displayName, categoryName, titleManuallyEdited]);

  const canNext = (() => {
    switch (currentStep) {
      case 1:
        return items.length > 0;
      case 2:
        return displayName.trim().length > 0 && title.trim().length > 0;
      case 3:
        return winCondition !== "most_items" || !!timeLimitMinutes;
      default:
        return false;
    }
  })();

  const handleNext = () => {
    if (currentStep < 3) setCurrentStep((s) => (s + 1) as 1 | 2 | 3);
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep((s) => (s - 1) as 1 | 2 | 3);
  };

  const handleSubmit = async () => {
    if (!title.trim() || !displayName.trim() || items.length === 0) {
      setError("Please fill in all required fields.");
      return;
    }
    if (winCondition === "most_items" && !timeLimitMinutes) {
      setError("Time limit is required for 'Most items found' games.");
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const body: CreateGameRequest = {
        title: title.trim(),
        displayName: displayName.trim(),
        items,
        moderationMode,
        winCondition,
        ...(timeLimitMinutes
          ? { timeLimitMinutes: Number(timeLimitMinutes) }
          : {}),
        ...(votingEnabled ? { votingEnabled: true } : {}),
        ...(instructions.trim()
          ? { instructions: instructions.trim() }
          : {}),
        ...(templateId ? { templateId } : {}),
      };

      const res = await fetchJson<CreateGameResponse>("/games", {
        method: "POST",
        body,
      });

      addSession({
        gameId: res.gameId,
        slug: res.slug,
        gameTitle: title.trim(),
        playerToken: res.token,
        playerId: res.playerId,
        displayName: displayName.trim(),
        role: "owner",
        isModerator: true,
        joinedAt: Date.now(),
      });

      navigate(`/game/${res.gameId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create game");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-[calc(100dvh-4rem)] flex-col">
      <Modal
        open={!ageConfirmed}
        title="Age Confirmation"
        message="You must be 13 or older to create a game on ISPYFR."
        confirmLabel="I am 13 or older"
        onConfirm={() => {
          sessionStorage.setItem(AGE_CONFIRMED_KEY, "true");
          setAgeConfirmed(true);
        }}
        onCancel={() => navigate("/")}
      />

      <div className="px-4 pt-4">
        <WizardStepper currentStep={currentStep} />
      </div>

      <div className="flex-1 px-4 py-6">
        <div className="mx-auto max-w-lg">
          {currentStep === 1 && (
            <StepItems
              items={items}
              onItemsChange={setItems}
              onCategorySelected={setCategoryName}
              onTemplateSelected={setTemplateId}
            />
          )}
          {currentStep === 2 && (
            <StepNameIdentity
              displayName={displayName}
              onDisplayNameChange={setDisplayName}
              title={title}
              onTitleChange={setTitle}
              titleManuallyEdited={titleManuallyEdited}
              onTitleManualEdit={() => setTitleManuallyEdited(true)}
              onTitleClear={() => setTitleManuallyEdited(false)}
            />
          )}
          {currentStep === 3 && (
            <StepSettings
              winCondition={winCondition}
              onWinConditionChange={setWinCondition}
              timeLimitMinutes={timeLimitMinutes}
              onTimeLimitChange={setTimeLimitMinutes}
              moderationMode={moderationMode}
              onModerationModeChange={setModerationMode}
              votingEnabled={votingEnabled}
              onVotingEnabledChange={setVotingEnabled}
              instructions={instructions}
              onInstructionsChange={setInstructions}
            />
          )}
          {error && (
            <p className="mt-4 text-base text-error">{error}</p>
          )}
        </div>
      </div>

      <WizardNav
        currentStep={currentStep}
        canNext={canNext}
        submitting={submitting}
        onBack={handleBack}
        onNext={handleNext}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
